import { useCallback, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
// import { observer } from "mobx-react-lite";
import { Util } from "@xt/xtbase";
import store from "store";
import SocketPrivate from "utils/socket/private";
import SocketLever from "utils/socket/lever";
import { get_balances, get_leverBalance } from "api/v4/balance";

import useTimeout from "hooks/useTimeout";
import useAxiosCancelFun from "hooks/useAxiosCancelFun";

import { BalancesProps, WsBalanceProps, WsOrderProps, OpenOrderProps } from "store/balances";
import { TradeOrderStateEnum } from "store/trade";

const { Big } = Util;

const useUserLogin = () => {
  const router = useRouter();
  const { name, type, isLever, getLeverMarketConfig } = store.market;
  const { currencyQuantity, currencyPrice, wsBalance, getAccountOverview, getOpenOrder } = store.balances;
  const { isLogin, token, leverWsUserKey, getTokenUserKey } = store.user;

  /**
   * Prop 交易页：必须在 currencies 中追加 usdx，否则 /balance/balances 拿不到 USDX 资产；
   * success 里用返回的 USDX 写入 currencyPrice（金额与币种均来自该接口，与 btc_usdx_cfd 展示一致）。
   */
  const appendUsdxForPropTradeBalances =
    typeof router.pathname === "string" && router.pathname.includes("/proptrading/trade");

  const apiReqBalanceSpotArg = useMemo(() => {
    // 对于 CFD，去掉 _cfd 后缀，只取 base 和 quote
    const symbolName = name.replace(/_cfd$/i, "");
    const pair = symbolName.split("_").slice(0, 2);
    const withUsdx =
      appendUsdxForPropTradeBalances && !pair.some((c) => String(c).toLowerCase() === "usdx")
        ? [...pair, "usdx"]
        : pair;
    const currencies = withUsdx.join(",");

    return {
      fn: get_balances,
      config: {
        params: {
          currencies,
        },
      },
      success: (data) => {
        const assets: BalancesProps[] = data.assets;
        // Prop 交易页：同一接口已带 usdx，报价余额以 USDX 资产为准
        if (appendUsdxForPropTradeBalances) {
          const baseCurrency = symbolName.split("_")[0];
          const baseDoc = baseCurrency ? assets.find((obj) => obj.currency === baseCurrency) : undefined;
          const usdxRaw = assets.find((obj) => String(obj.currency).toLowerCase() === "usdx");
          const usdxDoc = usdxRaw
            ? { ...usdxRaw, currency: String(usdxRaw.currency).toUpperCase() }
            : null;
          store.balances.updateState({
            currencyQuantity: baseDoc || null,
            currencyPrice: usdxDoc,
          });
          return;
        }
        // 只取前两部分（base 和 quote），忽略 _cfd 后缀
        symbolName.split("_").slice(0, 2).map((currency, index) => {
          const doc = assets.find((obj) => obj.currency === currency);
          store.balances.updateState({
            [index ? "currencyPrice" : "currencyQuantity"]: doc || null,
          });
        });
        // console.log("store.balances", store.balances);
      },
    };
  }, [name, appendUsdxForPropTradeBalances]);
  const apiReqBalanceSpot = useAxiosCancelFun(apiReqBalanceSpotArg);

  const apiReqBalanceLeverArg = useMemo(() => {
    return {
      fn: get_leverBalance,
      config: {
        params: {
          symbol: name,
        },
      },
      success: (data) => {
        store.balances.updateState({
          currencyQuantity: data.base || null,
          currencyPrice: data.quote || null,
        });
        // console.log("store.balances", store.balances);
      },
    };
  }, [name]);
  const apiReqBalanceLever = useAxiosCancelFun(apiReqBalanceLeverArg);

  const [apiReqLeverMarketConfig, apiReqLeverMarketConfigClear] = useTimeout(getLeverMarketConfig, { ms: 5000, isLoop: true });
  const apiReqLeverAccountOverviewFn = useCallback(() => {
    return getAccountOverview(name);
  }, [name]);
  const [apiReqLeverAccountOverview, apiReqLeverAccountOverviewClear] = useTimeout(apiReqLeverAccountOverviewFn, { ms: 5000, isLoop: true, isInitExec: true });

  const wsCbBalance = useCallback(
    (data: WsBalanceProps) => {
      console.log("wsCbBalance===", data, store.market.type, store.market.name);
      store.balances.updateState({ wsBalance: data });

      if (data.z !== store.market.type) return;
      // 对于 CFD，去掉 _cfd 后缀，只取 base 和 quote
      const symbolName = store.market.name.replace(/_cfd$/i, "");
      const ary = symbolName.split("_").slice(0, 2); // 只取前两部分
      if (!ary.includes(data.c)) return;
      const availableAmount = Big(data.b || 0)
        .minus(data.f || 0)
        .toFixed();

      if (ary[0] === data.c) {
        store.balances.updateState({
          currencyQuantity: {
            ...(store.balances.currencyQuantity || { currency: ary[0] }),
            availableAmount,
          },
        });
      } else if (ary[1] === data.c) {
        store.balances.updateState({
          currencyPrice: {
            ...(store.balances.currencyPrice || { currency: ary[1] }),
            availableAmount,
          },
        });
      }
      // console.log("store.balances", store.balances);
    },
    // [type, name, currencyQuantity, currencyPrice]
    []
  );
  /*变化太快可能不渲染
  useEffect(() => {
    if (!wsBalance) return;
    console.log("wsCbBalance useEffect===", wsBalance, type, name);
    if (wsBalance.z !== type) return;
    const ary = name.split("_");
    if (!ary.includes(wsBalance.c)) return;
    const availableAmount = Big(wsBalance.b || 0)
      .minus(wsBalance.f || 0)
      .toFixed();

    if (ary[0] === wsBalance.c) {
      store.balances.updateState({
        currencyQuantity: {
          ...(currencyQuantity || { currency: ary[0] }),
          availableAmount,
        },
      });
    } else {
      store.balances.updateState({
        currencyPrice: {
          ...(currencyPrice || { currency: ary[1] }),
          availableAmount,
        },
      });
    }
  }, [wsBalance]);
   */
  const wsCbOrder = useCallback((data: WsOrderProps) => {
    console.log("wsCbOrder=", data);
    console.log("type=", store.market.type);
    console.log("openOrder=", store.balances.openOrder);
    // getOpenOrder();

    if (data.bt === store.market.type) {
      const openOrder = store.balances.openOrder ? [...store.balances.openOrder] : [];
      if (data.st === TradeOrderStateEnum.NEW) {
        openOrder.unshift(getOrderObj(data));
      } else if (data.st === TradeOrderStateEnum.PARTIALLY_FILLED) {
        const doc = openOrder.find((obj) => obj.orderId === data.i);
        if (doc) {
          doc.tradeBase = data.eq;
        } else {
          openOrder.unshift(getOrderObj(data));
        }
      } else {
        const index = openOrder.findIndex((obj) => obj.orderId === data.i);
        if (index >= 0) {
          openOrder.splice(index, 1);
        }
      }

      console.log("openOrder todo", openOrder);
      store.balances.updateState({ openOrder });
    }

    store.balances.updateState({ wsOrder: data });

    function getOrderObj(data): OpenOrderProps {
      return {
        symbol: data.s,
        orderId: data.i, //订单号
        time: data.ct,
        updatedTime: data.t,
        type: data.tp, //交易类型
        side: data.sd, //交易方向
        price: data.p,
        origQty: data.oq, //原始数量
        origQuoteQty: data.oqq, //原始金额
        tradeBase: data.eq, //成交数量
      };
    }
  }, []);

  useEffect(() => {
    store.balances.updateState({
      currencyQuantity: undefined,
      currencyPrice: undefined,
      leverAccountOverview: undefined,
    });
  }, [name, type]);
  useEffect(() => {
    if (!isLogin) return;
    if (!isLever) {
      apiReqBalanceSpot();
    } else {
      apiReqBalanceLever();
      apiReqLeverMarketConfig();
      apiReqLeverAccountOverview();
      !leverWsUserKey && getTokenUserKey();
    }

    return () => {
      if (isLever) {
        apiReqLeverMarketConfigClear();
        apiReqLeverAccountOverviewClear();
      }
    };
  }, [isLogin, name, isLever]);
  // private 通道统一管理：balance、order、cfd_position_closed（差价合约平仓推送，不考虑现货）
  const wsCbCfdPositionClosed = useCallback((data: { pi?: string | number }) => {
    const positionId = data?.pi;
    if (positionId == null) return;
    store.trade.updateState({ cfdPositionClosedPositionId: String(positionId) });
  }, []);

  useEffect(() => {
    if (!isLogin || !token) return;
    SocketPrivate.addChannel({ event: `balance` }, wsCbBalance);
    SocketPrivate.addChannel({ event: `order` }, wsCbOrder);
    SocketPrivate.addChannel({ event: `cfd_position_closed` }, wsCbCfdPositionClosed);

    return () => {
      SocketPrivate.removeChannel({ event: `balance` });
      SocketPrivate.removeChannel({ event: `order` });
      SocketPrivate.removeChannel({ event: `cfd_position_closed` });
      store.balances.updateState({ wsOrder: undefined });
      store.balances.updateState({ wsBalance: undefined });
      store.trade.updateState({ cfdPositionClosedPositionId: undefined });
    };
  }, [isLogin, token]);
  useEffect(() => {
    if (!isLogin || !leverWsUserKey) return;
    if (!isLever) return SocketLever.close();

    const msg = { channel: `liquidationRate`, marketName: name };
    SocketLever.addChannel(msg, (leverWsLiquidationRate) => {
      store.trade.updateState({ leverWsLiquidationRate });
    });

    return () => {
      SocketLever.removeChannel(msg);
      store.trade.updateState({ leverWsLiquidationRate: undefined });
    };
  }, [isLogin, leverWsUserKey, isLever, name]);
  useEffect(() => {
    if (!isLogin) return;
    // CFD 页面不调用 open-order 接口（这是现货的订单接口）
    const isCfd = name && name.toLowerCase().endsWith("_cfd");
    if (isCfd) {
      return;
    }
    getOpenOrder();

    return () => {
      store.balances.updateState({ openOrder: undefined });
    };
  }, [isLogin, type, name]);

  // 余额定时刷新（与仓位列表轮询间隔一致，CFD 下单后也会通过 refreshBalance 主动拉一次）
  const refreshBalance = useCallback(() => {
    apiReqBalanceSpot();
  }, [apiReqBalanceSpot]);
  useEffect(() => {
    if (!isLogin) return;
    const BALANCE_POLL_MS = 15000;
    const timer = setInterval(refreshBalance, BALANCE_POLL_MS);
    return () => clearInterval(timer);
  }, [isLogin, refreshBalance]);

  return { refreshBalance };
};

export default useUserLogin;
